import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import { getShortId } from "@/lib/string";
import { pluralise } from "@/lib/utils";
import { useDirectoryStructure } from "@/services/directories.service";
import InputText from "@components/atoms/InputText";
import AppDialog, { DialogLabel, DialogSection } from "@components/library/AppDialog";
import Button from "@components/library/Button";
import { FileLabelWithPopover } from "@components/library/Entities/FileLabelWithPopover";
import { ArrowRightIcon, ChevronDownIcon } from "@components/library/Icons";
import { ToastVariant, hlToast } from "@components/library/Toast";
import { FilePicker } from "@components/llama/FilePicker/FilePicker";
import { useRouter } from "next/router";
import { useState } from "react";
import { FileVersionSaveHandler, SaveVersionDialog } from "../SaveVersionDialog";
import { ExistingSelectedTarget, NewSelectedTarget, SelectedTarget } from "./useDatasetDialog";
import { addDatapointsFromLogs, upsertDataset } from "@/services/datasets.service";
import { Dataset } from "@/types/app/dataset";
import { refetchVersionsForFile } from "@/services/files.service";
import { DEFAULT_DATASET } from "@/lib/defaults/datasets";
import { getFileHref } from "@/lib/path-utils";

interface DatasetFromLogDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onClose: () => void;
  onCreate: () => void;
  logIds: string[];
  selectedTarget: SelectedTarget;
  setSelectedTarget: (target: SelectedTarget) => void;
}

export const DatasetFromLogDialog = (props: DatasetFromLogDialogProps) => {
  const { logIds, onCreate, onClose, selectedTarget, setSelectedTarget } = props;
  const router = useRouter();
  const { slug } = useActiveOrganization();
  const [step, setStep] = useState<"choose" | "save">("choose");
  const [isPickingDatasetVersion, setIsPickingDatasetVersion] = useState(false);
  const { onChange: onDirectoriesChange, mutate: mutateDirectoryStructure } = useDirectoryStructure();

  if (!props.open) {
    return null;
  }

  const handleCreateNew = async (name: string, versionName: string | null, versionDescription: string | null) => {
    try {
      // Create a new Dataset to add the Logs to.
      const newDataset = (
        await upsertDataset({
          path: name,
          ...DEFAULT_DATASET,
          // TODO(v5): Consider if we want to create an uncommitted version here.
          // Currently, this will result in 2 saved versions (as Logs can only be added to an existing Dataset
          // and this upsert call creates an unnecessary version).
          // It is possible for this to be an uncommitted version that will mostly not show up in the UI,
          // but that might cause issues down the line where we don't expect Datasets to have uncommitted versions.
          version_name: versionName,
          version_description: versionDescription,
        })
      ).data;
      // Add the Logs to the new Dataset.
      const datasetVersion = await addDatapointsFromLogs(newDataset.id, newDataset.version_id, logIds, versionName);
      const datasetUrl = getFileHref({ slug, id: newDataset.id, page: "editor" }) + `/${datasetVersion.version_id}`;
      // Refresh the sidebar
      mutateDirectoryStructure();

      hlToast({
        title: `Dataset created with ${datasetVersion.datapoints_count} Datapoints`,
        description: (
          <div className="flex items-center gap-4">
            Click to view <ArrowRightIcon className="h-12 w-12 text-oldgray-700" />
          </div>
        ),
        variant: ToastVariant.Success,
        onClick: () => {
          router.push(datasetUrl);
        },
      });
      onCreate();
      onClose();
      // Set the selected target to the new dataset version for convenience of future adding.
      setSelectedTarget({ type: "add", version: datasetVersion });
      return datasetVersion;
    } catch (e: any) {
      // TODO: this seems to 500 rather than handle it properly. To fix.
      if (e.response?.status === 409) {
        hlToast({
          title: "Dataset already exists with that name",
          description: e.response?.data?.detail || `Contact us for more details.`,
          variant: ToastVariant.Error,
        });
        setStep("choose");
        return;
      } else {
        console.log("Error creating dataset", e);
        hlToast({
          title: "Failed to create dataset",
          description: e.response?.data?.detail || `Contact us for more details.`,
          variant: ToastVariant.Error,
        });
      }
    }
  };

  const handleAddToExisting = async (version: Dataset, versionName: string | null) => {
    try {
      const newVersion = await addDatapointsFromLogs(version.id, version.version_id, logIds, versionName);
      const datasetUrl = getFileHref({ slug, id: version.id, page: "editor" }) + `/${newVersion.version_id}`;

      refetchVersionsForFile(version.id); // Refresh the version picker.

      hlToast(
        {
          title: `Added ${newVersion.datapoints_count - version.datapoints_count} datapoints to ${version.name}`,
          description: (
            <div className="flex items-center gap-4">
              Click to view <ArrowRightIcon className="h-12 w-12 text-oldgray-700" />
            </div>
          ),
          variant: ToastVariant.Success,
          onClick: () => {
            router.push(datasetUrl);
          },
        },
        { duration: 10000 },
      );
      onCreate();
      onClose();
      return newVersion;
    } catch (e: any) {
      console.log("Error adding to dataset", e);
      hlToast({
        title: "Failed to add to dataset",
        description: e.response?.data?.detail || `Contact us for more details.`,
        variant: ToastVariant.Error,
      });
    }
  };

  const handleSave: FileVersionSaveHandler = async (versionName, versionDescription) => {
    // Check that the selected target is valid (i.e. has a name or version)
    if (selectedTarget.type === "new") {
      if (!selectedTarget.name) {
        throw new Error("Invalid selected target");
      }
      // Reset state.
      setStep("choose");
      setIsPickingDatasetVersion(false);
      props.setOpen(false);

      return handleCreateNew(selectedTarget.name, versionName, versionDescription);
    } else {
      if (!selectedTarget.version) {
        throw new Error("Invalid selected target");
      }
      // Reset state.
      setStep("choose");
      setIsPickingDatasetVersion(false);
      props.setOpen(false);

      return handleAddToExisting(selectedTarget.version, versionName);
    }
  };

  if (isPickingDatasetVersion) {
    return (
      <FilePicker
        open={true}
        onClose={() => setIsPickingDatasetVersion(false)}
        fileTypeFilter="dataset"
        onVersionSubmitted={({ version }) => {
          if (version.type !== "dataset") {
            throw new Error("Expected dataset version");
          }
          setSelectedTarget({ type: "add", version });
          setIsPickingDatasetVersion(false);
        }}
      />
    );
  }

  return step === "choose" ? (
    <ChooseStep setStep={setStep} setIsPickingDatasetVersion={setIsPickingDatasetVersion} {...props} />
  ) : (
    <SaveVersionDialog
      open={step === "save"}
      showOutcome={false}
      onClose={() => {
        props.onClose();
        setStep("choose");
      }}
      handleSave={async (versionName, versionDescription) => {
        const newVersion = await handleSave(versionName, versionDescription);
        // Set the selected target to the new Dataset Version for convenience of future adding.
        // (To avoid adding datapoints to the same fixed version)
        setSelectedTarget({ type: "add", version: newVersion as Dataset | undefined });
        return newVersion;
      }}
      initialDescription={
        selectedTarget?.type === "new"
          ? `Create dataset with ${logIds.length} datapoints. Log IDs: ${logIds.map((id) => getShortId(id)).join(", ")}`
          : `Added ${logIds.length} datapoints. Log IDs: ${logIds.map((id) => getShortId(id)).join(", ")}`
      }
    />
  );
};

interface ChooseStepProps {
  open: boolean;
  logIds: string[];
  setStep: React.Dispatch<React.SetStateAction<"choose" | "save">>;
  selectedTarget: SelectedTarget;
  setSelectedTarget: (target: SelectedTarget) => void;
  setIsPickingDatasetVersion: (value: boolean) => void;
  onClose: () => void;
}

const ChooseStep = ({
  open,
  onClose,
  logIds,
  setStep,
  selectedTarget,
  setSelectedTarget,
  setIsPickingDatasetVersion,
}: ChooseStepProps) => {
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={
        selectedTarget.type === "new"
          ? `Create Dataset with ${logIds.length} Datapoint${pluralise(logIds.length)}`
          : `Add ${logIds.length} Datapoint${pluralise(logIds.length)} to Dataset`
      }
      description={
        selectedTarget.type === "new" ? (
          <>
            Create a new Dataset from the selected Logs. <br />
            The new Dataset will be created in the Home directory.
          </>
        ) : (
          <>
            Add the selected Datapoints to an existing Dataset. <br />A new Version of the choosen Dataset will be
            created.
          </>
        )
      }
    >
      <div className="flex min-h-[140px] flex-col">
        {selectedTarget.type === "new" ? (
          <CreateDatasetForm
            onSubmit={() => setStep("save")}
            selectedTarget={selectedTarget}
            setSelectedTarget={setSelectedTarget}
          />
        ) : (
          <AddToExistingDataset
            selectedTarget={selectedTarget}
            setSelectedTarget={setSelectedTarget}
            setIsPickingDatasetVersion={setIsPickingDatasetVersion}
            onSubmit={() => setStep("save")}
            onClose={onClose}
          />
        )}
      </div>
    </AppDialog>
  );
};

interface CreateDatasetFormProps {
  onSubmit: () => void;
  selectedTarget: NewSelectedTarget;
  setSelectedTarget: (target: SelectedTarget) => void;
}

const CreateDatasetForm = ({ onSubmit, selectedTarget, setSelectedTarget }: CreateDatasetFormProps) => {
  return (
    <>
      <DialogSection>
        <DialogLabel>
          <div className="flex items-center justify-between gap-8">
            <span>Name</span>
            <Button
              tabIndex={0}
              styling="link"
              size={24}
              onClick={() =>
                setSelectedTarget({
                  type: "add",
                  version: undefined,
                })
              }
            >
              or add to existing dataset
            </Button>
          </div>
        </DialogLabel>
        <div className="mb-32">
          <InputText
            value={selectedTarget.name}
            placeholder="Enter dataset name"
            onChange={(event) => {
              event.stopPropagation();
              setSelectedTarget({ type: "new", name: event.target.value });
            }}
          />
        </div>
      </DialogSection>
      <div className="grow" />
      <DialogSection>
        <Button
          onClick={() => {
            onSubmit();
          }}
          size={32}
          styling="solid"
          disabled={selectedTarget.name === ""}
        >
          Create Dataset
        </Button>
      </DialogSection>
    </>
  );
};

interface AddToExistingDatasetProps {
  onSubmit: () => void;
  selectedTarget: ExistingSelectedTarget;
  setSelectedTarget: (target: SelectedTarget) => void;
  setIsPickingDatasetVersion: (value: boolean) => void;
  onClose: () => void;
}

const AddToExistingDataset = ({
  onSubmit,
  selectedTarget,
  setSelectedTarget,
  setIsPickingDatasetVersion,
}: AddToExistingDatasetProps) => {
  return (
    <>
      <DialogSection>
        <DialogLabel>
          <div className="flex items-center justify-between gap-8">
            <span>Select Dataset</span>
            <Button
              tabIndex={0}
              styling="link"
              size={24}
              onClick={() => {
                setSelectedTarget({
                  type: "new",
                  name: "",
                });
              }}
            >
              or create new dataset
            </Button>
          </div>
        </DialogLabel>
        <div className="mb-32">
          <Button
            IconRight={ChevronDownIcon}
            onClick={() => setIsPickingDatasetVersion(true)}
            size={32}
            styling="outline"
          >
            {selectedTarget.version == undefined ? (
              "Choose Dataset"
            ) : (
              <FileLabelWithPopover
                file={selectedTarget.version}
                size={20}
                displayFile
                tooltipContentProps={{
                  align: "start",
                  side: "bottom",
                }}
              />
            )}
          </Button>
        </div>
      </DialogSection>

      <div className="grow" />
      <DialogSection>
        <Button onClick={onSubmit} size={32} styling="solid" disabled={!selectedTarget.version}>
          Add to Dataset
        </Button>
      </DialogSection>
    </>
  );
};
